/* eslint-disable @next/next/no-css-tags */
import ErrorMessage from "components/ErrorMessage";
import { InputField } from "components/Forms/FormField";
import CheckAvailabilitySchema from "components/Forms/Schema/CheckAvailabilitySchema";
import Loading from "components/Loading";
import Router from "next/router";
import { useState } from "react";
import { isValidSchema } from "utils/SchemaUtils";
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Captcha from "components/Captcha/Captcha";
import { phoneNumberWithDash } from "utils/commonUtils";

const contactMethodEnum = {
    TEXT: "text",
    PHONE: "phone",
    EMAIL: "email",
};

const initState = {
    firstNameId: "",
    lastNameId: "",
    messageId: "Please contact me regarding the availability of this vehicle.",
    fromEmailId: "",
    phoneId: "",
    contactMethod: contactMethodEnum.TEXT,
    agreeid: false,
    captchaVerified: false,
};

const CheckAvailabilityForm = ({
    show,
    onCloseModal,
    data,
}: {
    show?: boolean;
    onCloseModal: any;
    data: any;
}) => {
    const [record, setRecord] = useState(initState);
    const [readMore, setReadMore] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const onChangeRecord = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        e.preventDefault();

        const { name } = e.target;
        let { value } = e.target;

        if(name === 'phoneId')
            value = phoneNumberWithDash(value);

        setRecord({
            ...record,
            [name]: value,
        });
    };

    const onChangeContactMethod = (value: string) => {
        setRecord({
            ...record,
            contactMethod: value,
        });
    };

    const onSubmit = async () => {
        if (!isValid) return;
        setSaving(true);

        const prospect: {
            firstName: string;
            lastName: string;
            cellPhone?: string;
            email?: string;
        } = {
            firstName: record.firstNameId,
            lastName: record.lastNameId,
            cellPhone: record.phoneId,
            email: record.fromEmailId,
        };

        if (prospect.cellPhone === '') {
            delete prospect.cellPhone;
        }

        if (prospect.email === '') {
            delete prospect.email;
        }

        const recordToSave = {
            lotId: data.lotId,
            leadSourceId: data.leadSourceId,
            note: record.messageId,
            prospectInfo: prospect,
            vehicleInfo: {
                stock: data.stock,
                year: data.year,
                make: data.make,
                model: data.model,
                trim: data.trim,
                price: data.price,
            }
        };

        try {
            const response = await fetch('/api/leads/checkavailability', {
                method: 'POST',
                body: JSON.stringify({ record: recordToSave }),
                headers: { 'Content-Type': 'application/json' },
            }).then((response) => response.json());

            const { success, message } = response?.data;
            setSaving(false);

            if (success) {                
                Router.push("/makemydealrequestresponse");
            }
            setSubmitError(message);
        } catch (error: any) {
            setSaving(false);
            setSubmitError(error?.message || error);
        }
    };

    const {
        firstNameId,
        lastNameId,
        messageId,
        fromEmailId,
        phoneId,
        contactMethod,
        agreeid,
    } = record;

    const { isValid } = isValidSchema(CheckAvailabilitySchema, record);

    const closeBtn = (
        <button
            type="button"
            className="close btn-close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={onCloseModal}
        >
            <span aria-hidden="true">&times;</span>
        </button>
    );

    const onValidateCaptcha = async (val: boolean) => {
        setRecord({
            ...record,
            captchaVerified: val,
        });
    };

    return (
        <>
            <link rel="preload" href="/static/styles/checkAvailabilityform.min.css" as="style" />
            <link rel="stylesheet" href="/static/styles/checkAvailabilityform.min.css" />

            <Modal className="check-availability-modal" isOpen={show} backdrop="static" toggle={() => onCloseModal()}>
                <ModalHeader close={closeBtn}>                    
                    Check Availability
                </ModalHeader>
                <ModalBody>
                    <div
                        className="gray-form row reset_css p-10 col-md-12"
                        data-parsley-validate="true"
                    >
                        <div className="row">
                            <div className="col-md-6">
                                <label
                                    className="control-label"
                                    style={{ marginBottom: "3px", paddingLeft: "15px" }}
                                >
                                    Preferred Contact Method
                                </label>
                                <div
                                    className="col-md-12 form-group"
                                    style={{ display: "flex" }}
                                >
                                    <label className="radio active">
                                        <input
                                            type="radio"
                                            name="contactMethod"
                                            checked={contactMethod === contactMethodEnum.TEXT}
                                            onChange={() =>
                                                onChangeContactMethod(contactMethodEnum.TEXT)
                                            }
                                        />
                                        Text
                                    </label>
                                    <label className="radio">
                                        <input
                                            type="radio"
                                            name="contactMethod"
                                            checked={contactMethod === contactMethodEnum.PHONE}
                                            onChange={() =>
                                                onChangeContactMethod(contactMethodEnum.PHONE)
                                            }
                                        />
                                        Phone Call
                                    </label>
                                    <label className="radio">
                                        <input
                                            type="radio"
                                            name="contactMethod"
                                            checked={contactMethod === contactMethodEnum.EMAIL}
                                            onChange={() =>
                                                onChangeContactMethod(contactMethodEnum.EMAIL)
                                            }
                                        />
                                        Email
                                    </label>
                                </div>
                                <div className="col-md-12 form-group">
                                    <label className="control-label" htmlFor="firstNameId">
                                        First Name
                                    </label>
                                    <InputField
                                        formSchema={CheckAvailabilitySchema}
                                        record={record}
                                        className="input-sm form-control"
                                        type="text"
                                        placeholder="Your First Name"
                                        name="firstNameId"
                                        value={firstNameId}
                                        onChange={onChangeRecord}
                                    />
                                </div>
                                <div className="col-md-12 form-group">
                                    <label className="control-label" htmlFor="lastNameId">
                                        Last name
                                    </label>
                                    <InputField
                                        formSchema={CheckAvailabilitySchema}
                                        record={record}
                                        className="input-sm form-control"
                                        type="text"
                                        placeholder="Your Last Name"
                                        name="lastNameId"
                                        value={lastNameId}
                                        onChange={onChangeRecord}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="col-md-12 form-group">
                                    <div>
                                        <label className="control-label" htmlFor="messageId">
                                            Message
                                        </label>
                                        <textarea
                                            rows={4}
                                            className="input-sm form-control"
                                            placeholder="What questions can we answer for you?"
                                            name="messageId"
                                            value={messageId}
                                            onChange={onChangeRecord}
                                        />
                                    </div>
                                </div>
                                {contactMethod === contactMethodEnum.EMAIL && (
                                    <div className="col-md-12 form-group contactbyemail ">
                                        <label className="control-label" htmlFor="fromEmailId">
                                            Email
                                        </label>
                                        <InputField
                                            formSchema={CheckAvailabilitySchema}
                                            record={record}
                                            className="input-sm form-control"
                                            type="email"
                                            placeholder="johndoe@email.com"
                                            name="fromEmailId"
                                            value={fromEmailId}
                                            onChange={onChangeRecord}
                                        />
                                    </div>
                                )}
                                {contactMethod !== contactMethodEnum.EMAIL && (
                                    <div className="col-md-12 form-group contactbyphone">
                                        <div>
                                            <label className="control-label" htmlFor="phoneId">
                                                Phone
                                            </label>
                                            <InputField
                                                formSchema={CheckAvailabilitySchema}
                                                record={record}
                                                className="input-sm form-control"
                                                type="tel"
                                                placeholder="Phone"
                                                name="phoneId"
                                                value={phoneId}
                                                onChange={onChangeRecord}
                                            />
                                        </div>
                                        <span className="contactbysms">
                                            Please send me a message (Msg & Data rates may apply).
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12 form-group">
                                <label className="checkbox-inline m-l-10" htmlFor="agreeid"

                                    style={{ overflow: 'auto' }}
                                >
                                    <input type="checkbox"
                                        checked={agreeid}
                                        id="agreeid"
                                        onChange={() => { setRecord({ ...record, agreeid: !record.agreeid }); }}
                                    />
                                    <span id="privacy-policy-agree" className={`chk-label-agree ${readMore ? 'agreedcontentfull' : 'agreedcontentshort'}`}>
                                        *By checking this box, I agree to receiving, emails, calls, and text messages about vehicles and related products from
                                        our company at the number provided or via my other
                                        contact information provided above. This consent does
                                        not require me to buy anything. I also acknowledge that
                                        I have read and agree to the{" "}
                                        <a href="/privacypolicy" target="_blank">
                                            Terms of Use and Privacy Policy.
                                        </a>{" "}
                                        Standard message and data rates may apply
                                    </span>
                                </label>
                                <div
                                    className="col-md-12 text-right"
                                    style={{ marginTop: "-15px" }}
                                >
                                    {readMore && (
                                        <span
                                            className="readlesscainfo"
                                            onClick={() => setReadMore(false)}
                                        >
                                            <i className="fa fa-angle-double-up"></i> read less
                                        </span>
                                    )}
                                    {!readMore && (
                                        <span
                                            className="readmorecainfo"
                                            onClick={() => setReadMore(true)}
                                        >
                                            read more <i className="fa fa-angle-double-down"></i>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12 form-group">
                                <Captcha
                                    onValidateCaptcha={(val: boolean)=> onValidateCaptcha(val)}
                                />
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            {!isValid && (<div><ErrorMessage message="Please fill all the required fields!." /></div>)}
                            {!record.captchaVerified && (<div><ErrorMessage message="Please solve the captcha." /></div>)}
                            <button disabled={isSaving || !isValid || !record.captchaVerified} onClick={onSubmit} className="button red width-100">
                                {isSaving ? <Loading text='processing...' /> : 'Send your message'}
                            </button>
                            {submitError?.length > 0 && (<div><ErrorMessage message={submitError} /></div>)}
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default CheckAvailabilityForm;
